import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import type { Message } from '@/components/chat/chat-types';
import type { UniversalChatContextPayload } from '@/components/chat/universal-chat-context';
import type { SummaryImage } from '@/lib/media/summary-images';
import type { Tweet as TweetType } from '@/types/tweet';
import type { InlineTweetReference, StreamState, SummarizeOverrides, SummaryContextSnapshot } from '../types';
import { useExplainRequest } from './useExplainRequest';
import { useShowMoreVisibility } from './useShowMoreVisibility';
import { useSummarizeRequest } from './useSummarizeRequest';
import type { SummaryBaseState, SummarySettingsActions } from './useSummarySettings';
export interface UseSummaryStreamsParameters {
  actions: SummarySettingsActions;
  appendMessages: (messages: Message[]) => void;
  buildPrompt: (custom: string, includeImages: boolean) => string;
  includeImagesInPrompt: boolean;
  inlineTweetUrls: InlineTweetReference[];
  isChatEnabled: boolean;
  longFormHint: boolean;
  openChat: () => void;
  registerContext: (context: UniversalChatContextPayload) => void;
  setSummaryContextSnapshot: (snapshot: SummaryContextSnapshot | null) => void;
  settings: SummaryBaseState;
  summaryImages: SummaryImage[];
  tweet: TweetType;
}
export interface UseSummaryStreamsResult {
  canExplain: boolean;
  canSummarize: boolean;
  explain: () => Promise<void>;
  explanationState: StreamState;
  handleShowMoreVisibility: (visible: boolean) => void;
  hasSummary: boolean;
  isExplaining: boolean;
  isSummarizing: boolean;
  runActiveMode: () => Promise<void>;
  shouldRenderSummaryControls: boolean;
  showMoreVisible: boolean;
  submitSettings: () => Promise<void>;
  summarize: (overrides?: SummarizeOverrides) => Promise<void>;
  summarizeButtonLabel: string;
  summaryState: StreamState;
}
export function useSummaryStreams(parameters) {
  const $ = _c(53);
  const {
    showMoreVisible,
    handleShowMoreVisibility
  } = useShowMoreVisibility(Boolean(parameters.longFormHint));
  let t0;
  if ($[0] !== parameters.buildPrompt || $[1] !== parameters.includeImagesInPrompt || $[2] !== parameters.setSummaryContextSnapshot || $[3] !== parameters.settings || $[4] !== parameters.summaryImages.length || $[5] !== parameters.tweet.id) {
    t0 = {
      tweetId: parameters.tweet.id,
      settings: parameters.settings,
      includeImagesInPrompt: parameters.includeImagesInPrompt,
      summaryImageCount: parameters.summaryImages.length,
      buildPrompt: parameters.buildPrompt,
      setSummaryContextSnapshot: parameters.setSummaryContextSnapshot
    };
    $[0] = parameters.buildPrompt;
    $[1] = parameters.includeImagesInPrompt;
    $[2] = parameters.setSummaryContextSnapshot;
    $[3] = parameters.settings;
    $[4] = parameters.summaryImages.length;
    $[5] = parameters.tweet.id;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const summary = useSummarizeRequest(t0);
  let t1;
  if ($[7] !== parameters.appendMessages || $[8] !== parameters.includeImagesInPrompt || $[9] !== parameters.inlineTweetUrls || $[10] !== parameters.isChatEnabled || $[11] !== parameters.openChat || $[12] !== parameters.registerContext || $[13] !== parameters.settings || $[14] !== parameters.summaryImages || $[15] !== parameters.tweet) {
    t1 = {
      tweet: parameters.tweet,
      summaryImages: parameters.summaryImages,
      inlineTweetUrls: parameters.inlineTweetUrls,
      includeImagesInPrompt: parameters.includeImagesInPrompt,
      settings: parameters.settings,
      isChatEnabled: parameters.isChatEnabled,
      openChat: parameters.openChat,
      registerContext: parameters.registerContext,
      appendMessages: parameters.appendMessages
    };
    $[7] = parameters.appendMessages;
    $[8] = parameters.includeImagesInPrompt;
    $[9] = parameters.inlineTweetUrls;
    $[10] = parameters.isChatEnabled;
    $[11] = parameters.openChat;
    $[12] = parameters.registerContext;
    $[13] = parameters.settings;
    $[14] = parameters.summaryImages;
    $[15] = parameters.tweet;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  const explanation = useExplainRequest(t1);
  const canSummarize = Boolean(parameters.longFormHint) || showMoreVisible;
  const canExplain = canSummarize;
  let t2;
  bb0: {
    if (!canSummarize) {
      t2 = false;
      break bb0;
    }
    if (parameters.settings.summaryMode === "explain") {
      let t3;
      if ($[17] !== explanation.explanationState.status || $[18] !== explanation.explanationState.text) {
        t3 = !explanation.explanationState.text.trim() || explanation.explanationState.status === "error";
        $[17] = explanation.explanationState.status;
        $[18] = explanation.explanationState.text;
        $[19] = t3;
      } else {
        t3 = $[19];
      }
      t2 = t3;
      break bb0;
    }
    t2 = !summary.hasSummary || summary.summaryState.status === "error";
  }
  const shouldRenderSummaryControls = t2;
  let t3;
  bb1: {
    if (parameters.settings.summaryMode === "explain") {
      t3 = explanation.isExplaining ? "Explaining\u2026" : "Explain";
      break bb1;
    }
    if (parameters.settings.summaryMode === "custom") {
      t3 = summary.isSummarizing ? "Running prompt\u2026" : "Run custom prompt";
      break bb1;
    }
    t3 = summary.isSummarizing ? "Summarizing\u2026" : "Summarize";
  }
  const summarizeButtonLabel = t3;
  let t4;
  if ($[20] !== canSummarize || $[21] !== summary) {
    t4 = async overrides => {
      if (!canSummarize) {
        return;
      }
      await summary.summarize(overrides);
    };
    $[20] = canSummarize;
    $[21] = summary;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const guardedSummarize = t4;
  let t5;
  if ($[23] !== canExplain || $[24] !== explanation) {
    t5 = async () => {
      if (!canExplain) {
        return;
      }
      await explanation.explain();
    };
    $[23] = canExplain;
    $[24] = explanation;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const guardedExplain = t5;
  let t6;
  if ($[26] !== explanation.isExplaining || $[27] !== guardedExplain || $[28] !== guardedSummarize || $[29] !== parameters.actions || $[30] !== summary.isSummarizing) {
    t6 = async () => {
      if (summary.isSummarizing || explanation.isExplaining) {
        return;
      }
      const applied = parameters.actions.commitDrafts();
      if (applied.summaryMode === "explain") {
        await guardedExplain();
        return;
      }
      await guardedSummarize({
        summaryLengthIndex: applied.summaryLengthIndex,
        model: applied.summaryModel,
        customPrompt: applied.summaryMode === "custom" ? applied.customPrompt : "",
        summarySearchEnabled: applied.summarySearchEnabled
      });
    };
    $[26] = explanation.isExplaining;
    $[27] = guardedExplain;
    $[28] = guardedSummarize;
    $[29] = parameters.actions;
    $[30] = summary.isSummarizing;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  const submitSettings = t6;
  let t7;
  if ($[32] !== guardedExplain || $[33] !== guardedSummarize || $[34] !== parameters.settings.customPrompt || $[35] !== parameters.settings.summaryMode) {
    t7 = async () => {
      if (parameters.settings.summaryMode === "explain") {
        await guardedExplain();
        return;
      }
      await guardedSummarize({
        customPrompt: parameters.settings.summaryMode === "custom" ? parameters.settings.customPrompt : ""
      });
    };
    $[32] = guardedExplain;
    $[33] = guardedSummarize;
    $[34] = parameters.settings.customPrompt;
    $[35] = parameters.settings.summaryMode;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  const runActiveMode = t7;
  let t8;
  if ($[37] !== canExplain || $[38] !== canSummarize || $[39] !== explanation.explanationState || $[40] !== explanation.isExplaining || $[41] !== guardedExplain || $[42] !== guardedSummarize || $[43] !== handleShowMoreVisibility || $[44] !== runActiveMode || $[45] !== shouldRenderSummaryControls || $[46] !== showMoreVisible || $[47] !== submitSettings || $[48] !== summarizeButtonLabel || $[49] !== summary.hasSummary || $[50] !== summary.isSummarizing || $[51] !== summary.summaryState) {
    t8 = {
      summaryState: summary.summaryState,
      explanationState: explanation.explanationState,
      isSummarizing: summary.isSummarizing,
      isExplaining: explanation.isExplaining,
      canSummarize,
      canExplain,
      shouldRenderSummaryControls,
      summarizeButtonLabel,
      showMoreVisible,
      summarize: guardedSummarize,
      explain: guardedExplain,
      submitSettings,
      runActiveMode,
      handleShowMoreVisibility,
      hasSummary: summary.hasSummary
    };
    $[37] = canExplain;
    $[38] = canSummarize;
    $[39] = explanation.explanationState;
    $[40] = explanation.isExplaining;
    $[41] = guardedExplain;
    $[42] = guardedSummarize;
    $[43] = handleShowMoreVisibility;
    $[44] = runActiveMode;
    $[45] = shouldRenderSummaryControls;
    $[46] = showMoreVisible;
    $[47] = submitSettings;
    $[48] = summarizeButtonLabel;
    $[49] = summary.hasSummary;
    $[50] = summary.isSummarizing;
    $[51] = summary.summaryState;
    $[52] = t8;
  } else {
    t8 = $[52];
  }
  return t8;
}